/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import axios from 'axios';
import { useInvoiceEditorStore } from '@/modules/billing/invoice/services/useInvoiceEditorStore.js';
import { generateRandomID, hashArrayObjects } from '@/modules/billing/review/invoiceUtils';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { useTrustAccountingStore } from '@/services/trustAccountingStore.js';
import { useAccountsStore } from '@/services/UserService/useAccountsStore';
import { useMeStore } from '@/services/UserService/useMeStore';
import { getMatterLabel } from '@/modules/billing/utils/matterUtils';
import { formatCurrency } from '@/services/utils';
export const useInvoiceState = defineStore('invoiceState', {
  state: () => ({
    access: {},
    account: {},
    accountBalance: 0,
    active: false,
    attachments: {},
    billto: {},
    client: {},
    created: new Date(),
    credit: false,
    custom: [],
    config: {},
    deliveryStatus: 0,
    detail: '',
    entries: [],
    expenses: [],
    format: {},
    groups: [],
    missingGroups: [],
    hasExpenseAttachments: false,
    href: '',
    id: '',
    invoiceDate: new Date(),
    isQboEmail: false,
    invoicePdfDescription: '',
    isInvoiceDescriptionEnabled: false,
    ledes: false,
    matter: {},
    matters: [],
    memo: {},
    processed: false,
    reference: '',
    state: {},
    trust: {},
    totals: {},
    accounting: {},
    removedLineItems: [],
    eventsToTrack: [],
    isSplitBilling: false,
    isOrphanBilled: false,
    splitBillingConfig: {
      billDetails: []
    },
    debouncedFetchSplitBillingConfig: null,
    feeStates: {}
  }),
  getters: {
    hasGroupLineItemSelected: invoice => invoice.groups?.some(group => {
      return group.lineItems?.some(item => item.checked);
    }),
    hasCredit: invoice => {
      return invoice.groups?.some(group => group.lineItems?.some(lineItem => lineItem?.credit === true));
    },
    // A fee missing from feeStates was created in this editing session, so it is not billed
    // anywhere else yet: assignable, and nothing to warn about.
    isFeeAssignable: invoice => chargeId => invoice.feeStates[chargeId]?.assignable ?? true,
    hasTimeUnlockedWarning: invoice => lineItem => lineItem.entries?.some(entry => invoice.feeStates[entry.chargeId]?.timeUnlockedWarning === true) ?? false,
    getIsLocked: invoice => {
      if (!invoice || !invoice.state) return false;
      return !(invoice.state.draft || invoice.state.review || invoice.state.approved);
    },
    getHasInvoiceChanged: invoice => {
      // need to add wasModified when we change the invoice
      // and check if any of the line items was modified
      if (!invoice.groups) return false;
      return invoice.groups.some(g => {
        return g.lineItems.some(li => {
          if (li.entries) {
            if (li.entries.some(e => e.wasModified)) return true;
          }
          if (li.expenses) {
            if (li.expenses.some(e => e.wasModified)) return true;
          }
          if (li.charges) {
            if (li.charges.some(e => e.wasModified)) return true;
          }
        });
      });
    }
  },
  actions: {
    loadFromInvoice(invoice) {
      invoice.ledes = invoice.client.codes && invoice.client.codes.ledes;
      invoice.invoiceDate = new Date(invoice.invoiceDate);
      this.$reset();
      this.$patch({
        ...invoice
      });
      this.groups.forEach(group => {
        const isExpenseGroup = group.lineItems?.[0]?.expenses?.length > 0;
        group.includeUser = isExpenseGroup ? this.format?.includeUserInExpenseDescription : this.format?.includeUser;
        group.lineItems = group.lineItems.map(line => ({
          ...line,
          // Assign temporary ids to line items
          id: generateRandomID(),
          // Generate hashes of all entries, expenses, charges
          // This will be used to later save only those that changed
          entries: line.entries?.length ? hashArrayObjects(line.entries) : line.entries,
          expenses: line.expenses?.length ? hashArrayObjects(line.expenses) : line.expenses,
          charges: line.charges?.length ? hashArrayObjects(line.charges) : line.charges
        }));
      });
    },
    async getInvoiceContent({
      id
    }) {
      const {
        handleError
      } = useSnackbarHandler();
      try {
        const response = await axios.get(`/invoices/${id}/content?linkedItems=include`);
        this.loadFromInvoice(response.data);
        this.infoAddressIfThereIsNoBillTo();
      } catch (err) {
        handleError(err);
      }
    },
    async getFeeStates({
      id
    }) {
      const {
        handleError
      } = useSnackbarHandler();
      try {
        const {
          data
        } = await axios.get(`/invoices/${id}/fee-states`);
        this.feeStates = Object.fromEntries(data.fees.map(fee => [fee.chargeId, fee]));
      } catch (err) {
        handleError(err);
      }
    },
    infoAddressIfThereIsNoBillTo() {
      const invoiceEditorStore = useInvoiceEditorStore();
      if (!this.billto) {
        const addressPromise = this.matter ? invoiceEditorStore.getMatterBilling({
          id: this.matter.id
        }) : invoiceEditorStore.getClientBilling({
          id: this.client.id
        });
        addressPromise.then(({
          data: info
        }) => {
          this.billto = info.address;
        });
      }
    },
    addAdjustmentGroup() {
      const lineItem = {
        id: generateRandomID(),
        date: new Date(),
        amount: -0,
        description: 'Credit',
        credit: true
      };
      const group = this.group();
      this.groups.push(group.fromExisting({
        name: 'Adjustments',
        matter: null,
        lineItems: [lineItem]
      }));
      this.credit = true;
      return lineItem;
    },
    addGroup(group) {
      this.groups.push(group);
      this.$patch({
        groups: this.groups
      });

      //remove added group from missing group array
      let index = this.missingGroups.indexOf(g => g.type === group.type && g.matterId === group.matterId);
      if (index > -1) {
        this.missingGroups.splice(index, 1);
        this.$patch({
          missingGroups: this.missingGroups
        });
      }
    },
    group() {
      return {
        name: '',
        matter: null,
        lineItems: [],
        fromExisting(group) {
          this.name = group.name;
          this.matter = group.matter;
          this.lineItems = group.lineItems;
          return this;
        }
      };
    },
    updateLineItemsTotals(lineItem) {
      this.calculateGroupSubtotals();

      //Determine if admin fees need re calculation
      const entriesAmount = this.calculateEntriesAmount();
      const shouldRecomputeFees = lineItem?.entries && lineItem?.entries.length > 0;
      if (shouldRecomputeFees) {
        this.recomputeAdminFees(entriesAmount);
      }
      this.calculateTotalAmount();
      this.updateMemo();
    },
    // Helper function to calculate group subtotals
    calculateGroupSubtotals() {
      this.groups.forEach(group => {
        group.subtotal = group.lineItems.reduce((acc, li) => {
          return acc + (parseFloat(li.amount) || 0);
        }, 0);
      });
    },
    // Helper function to calculate total entries amount
    calculateEntriesAmount() {
      return this.groups.reduce((sum, group) => {
        const hasEntries = group.lineItems.some(li => li.entries && li.entries.length > 0);
        return sum + (hasEntries ? group.subtotal : 0);
      }, 0);
    },
    // Helper function to recompute admin fees
    recomputeAdminFees(entriesAmount) {
      this.groups.forEach(group => {
        const computedItems = group.lineItems.filter(li => li.computed);
        if (computedItems.length > 0) {
          computedItems.forEach(li => {
            if (typeof li.computed?.settings?.services !== 'undefined') {
              const percentage = li.computed.settings.services || 0;
              li.amount = Math.round(entriesAmount * percentage) / 100;
            }
          });

          // Update group subtotal after recomputing computed items
          group.subtotal = group.lineItems.reduce((acc, li) => {
            return acc + (parseFloat(li.amount) || 0);
          }, 0);
        }
      });
    },
    // Helper function to calculate total invoice amount
    calculateTotalAmount() {
      const totalAmount = this.groups.reduce((sum, group) => sum + group.subtotal, 0);
      this.totals.amount = Number(totalAmount.toFixed(2));

      // after calculation of the total amount, we need to call an api if isSplitBilling is true so that we can get the split billing config after the total amount is calculated
      if (this.isSplitBilling) {
        // Clear any existing debounced call to prevent race conditions
        if (this.debouncedFetchSplitBillingConfig) {
          clearTimeout(this.debouncedFetchSplitBillingConfig);
        }

        // Debounce the API call to prevent overlapping requests when totals change rapidly
        this.debouncedFetchSplitBillingConfig = setTimeout(async () => {
          await this.fetchSplitBillingConfig(this.totals.amount);
        }, 300); // Debounce delay of 300ms
      }
    },
    /**
     * Fetches split billing configuration from the backend API
     * @param {number} totalAmount - The total invoice amount to calculate split billing for
     * @returns {Promise<void>}
     */
    async fetchSplitBillingConfig(totalAmount) {
      const {
        handleError
      } = useSnackbarHandler();

      // Validate inputs
      if (!this.id) {
        console.error('Cannot fetch split billing config: invoice ID is missing');
        return;
      }
      if (totalAmount < 0) {
        console.warn('Split billing calculation skipped: negative total amount', totalAmount);
        return;
      }
      try {
        const response = await axios.get(`/invoices/${this.id}/split-billing-calculate`, {
          params: {
            totalAmount
          }
        });

        // Update the split billing config with the response data
        this.splitBillingConfig.billDetails = response.data || [];
      } catch (err) {
        handleError(err);
        console.error('Failed to fetch split billing configuration:', err);
      }
    },
    setTrust(account, evergreen) {
      this.$patch({
        trust: {
          account,
          evergreen
        }
      });
      if (this.$original) {
        this.$patch({
          $original: {
            trust: {
              account,
              evergreen
            }
          }
        });
      }
      this.updateMemo();
    },
    setAccountBalance(balance) {
      this.$patch({
        accountBalance: {
          balance
        }
      });
    },
    setFormat(format) {
      this.$patch({
        format
      });
    },
    setConfig(config) {
      this.$patch({
        config: config || {}
      });
    },
    setInvoiceDate(invoiceDate) {
      this.$patch({
        invoiceDate
      });
    },
    setInvoicePdfDescription(invoicePdfDescription) {
      this.$patch({
        invoicePdfDescription
      });
    },
    setInvoiceReference(reference) {
      this.$patch({
        reference
      });
    },
    // Helper function to generate trust memo content
    generateTrustMemo() {
      const {
        trustMemo
      } = useTrustAccountingStore();
      if (!this.trust || !this.trust.account || typeof trustMemo !== 'function') {
        return '';
      }
      return trustMemo({
        trustBalance: this.trust.account.balance,
        invoiceBalance: this.totals.amount - (this.discount || 0),
        accountBalance: this.accountBalance.balance,
        evergreen: this.trust.evergreen,
        config: this.config
      });
    },
    // Helper function to find matter from matters array by ID
    findMatterById(matterId) {
      if (!matterId || !this.matters || !Array.isArray(this.matters)) {
        return null;
      }
      return this.matters.find(matter => matter.id === matterId) || null;
    },
    // Helper function to get the current matter ID for filtering users
    getCurrentMatterId(formMatterId = null) {
      // Try to get matter ID from form first (if provided)
      if (formMatterId) {
        return formMatterId;
      }

      // Try to get matter ID from the invoice state
      if (this.matter?.id) {
        return this.matter.id;
      }

      // Fallback to first matter in matters array
      if (this.matters?.length > 0) {
        return this.matters[0].id;
      }

      // If no matter is available, return null (will disable matter filtering)
      return null;
    },
    // Helper function to generate matter totals memo content
    generateMatterTotalsMemo() {
      const meStore = useMeStore();
      const matterGroups = this.groups.reduce((groups, g) => {
        const id = g.matter?.id || (g.matter?.label ? `label_${g.matter.label}` : null) || (g.name ? `name_${g.name}` : 'unknown');
        if (!groups[id]) groups[id] = [];
        groups[id].push(g);
        return groups;
      }, {});
      const groupIds = Object.keys(matterGroups);
      if (groupIds.length === 0) {
        return '';
      }
      const term = meStore.terms?.Matter || 'Matter';
      let mattersMemo = `\n${term} Totals:\n`;
      groupIds.forEach(id => {
        if (!id || id === 'unknown') return;
        const groups = matterGroups[id];
        const group = groups[0];
        let matter = group?.matter;

        // If matter doesn't have a label, try to find it in the matters array
        if (matter && !matter.label && matter.id) {
          const foundMatter = this.findMatterById(matter.id);
          if (foundMatter) {
            matter = foundMatter;
          }
        }
        const matterLabel = getMatterLabel(matter, group?.name, id);
        const total = groups.reduce((sum, g) => sum + (g.subtotal || 0), 0);
        mattersMemo += `- ${matterLabel}: ${formatCurrency(total)}\n`;
      });
      return mattersMemo;
    },
    // Helper function to generate account totals memo content
    generateAccountTotalsMemo() {
      const accountsStore = useAccountsStore();
      const accountGroups = {};
      this.groups.forEach(g => g.lineItems.forEach(line => {
        if (line.account && line.hours && line.amount) {
          if (!accountGroups[line.account.id]) {
            accountGroups[line.account.id] = [];
          }
          accountGroups[line.account.id].push(line);
        }
      }));
      const sums = Object.keys(accountGroups).map(id => {
        const hours = Math.round(accountGroups[id].reduce((sum, l) => sum + l.hours, 0) * 100) / 100;
        return {
          id,
          hours,
          amount: accountGroups[id].reduce((sum, l) => sum + l.amount, 0),
          rate: accountGroups[id].reduce((sum, l) => sum + l.hours * l.rate.amount, 0) / hours
        };
      }).sort((a, b) => b.amount - a.amount);
      if (sums.length === 0) {
        return '';
      }
      let accountsMemo = '\nHours Summary:\n';
      sums.forEach(s => {
        const user = accountsStore.accountsList.find(u => u.id == s.id) || {};
        const name = user.name ? user.name + (user.initials ? ` (${user.initials})` : '') : 'Unknown';
        accountsMemo += `- ${name}: ${s.hours}h @${formatCurrency(s.rate)} = ${formatCurrency(s.amount)}\n`;
      });
      return accountsMemo;
    },
    generateCustomFieldsFromGroups() {
      let customFieldsMemo = '';
      const {
        client,
        matter
      } = this;
      const matters = this.groups.map(g => g.matter).filter(m => m && m.id).map(m => {
        const fullMatter = this.findMatterById(m.id);
        return fullMatter || m;
      }).filter(m => m.matterCustomFieldDetails?.length).reduce((acc, m) => {
        const id = m.id || m.label;
        if (!acc.some(i => (i.id || i.label) === id)) acc.push(m);
        return acc;
      }, []);
      if (client?.clientCustomFieldDetails?.length) {
        customFieldsMemo += `${client?.label}\n`;
        client?.clientCustomFieldDetails?.forEach(customField => {
          customFieldsMemo += `${customField?.customField?.name}: ${customField?.value}\n`;
        });
      }
      if (matter?.matterCustomFieldDetails?.length) {
        customFieldsMemo += `\n${matter?.label}\n`;
        matter?.matterCustomFieldDetails?.forEach(customField => {
          customFieldsMemo += `${customField?.customField?.name}: ${customField?.value}\n`;
        });
      }
      if (matters?.length) {
        const pieces = matters.filter(m => m.matterCustomFieldDetails.length).map(m => {
          let field = `\n${m.label} \n`;
          m.matterCustomFieldDetails.forEach(customField => {
            field += `${customField?.customField?.name}: ${customField?.value}\n`;
          });
          return field;
        });
        customFieldsMemo += pieces.join('--');
      }
      return customFieldsMemo.trim();
    },
    updateMemo() {
      const memo = {
        ...this.memo
      } || {};

      // Initialize memo sections
      memo.memo = memo.note ? `${memo.note}` : '';
      memo.trust = '';
      memo.matters = '';
      memo.accounts = '';
      memo.customFields = '';

      // Generate memo sections based on display preferences
      if (memo.showTrust) {
        memo.trust = this.generateTrustMemo();
      }
      if (memo.showMatterTotals) {
        memo.matters = this.generateMatterTotalsMemo();
      }
      if (memo.showCustomFields) {
        memo.customFields = this.generateCustomFieldsFromGroups();
      }
      if (memo.showAccountTotals) {
        memo.accounts = this.generateAccountTotalsMemo();
      }

      // Combine all memo parts
      const parts = [];
      if (memo.note) parts.push(memo.note);
      if (memo.showTrust) parts.push(memo.trust);
      if (memo.showMatterTotals) parts.push(memo.matters);
      if (memo.showCustomFields) parts.push(memo.customFields);
      if (memo.showAccountTotals) parts.push(memo.accounts);
      memo.memo = parts.filter(Boolean).join('\n\n');
      this.$patch({
        memo
      });
    },
    removeLineItem(lineItem, status, keepOnInvoice) {
      lineItem.status = status || lineItem.status;
      // Remove the line item and filter out empty groups in a single loop
      this.groups = this.groups.reduce((acc, group) => {
        // Remove the line item if it matches the id
        group.lineItems = group.lineItems.filter(li => li.id !== lineItem.id);

        // Add the group back only if it has line items
        if (group.lineItems.length > 0) {
          acc.push(group);
        }
        return acc;
      }, []);
      if (!keepOnInvoice) {
        this.removedLineItems = this.removedLineItems || [];
        this.removedLineItems.push(lineItem);
      }
      // Update the totals after removing the line item
      this.updateLineItemsTotals(lineItem);
    },
    updateLineItem(lineItem) {
      const invoiceEditorStore = useInvoiceEditorStore();
      if (!lineItem.status > 0) {
        lineItem.status = 1;
      }
      const deepCopyLineItem = item => {
        var copy = JSON.parse(JSON.stringify(item));
        copy.dateChanged = false;
        return copy;
      };

      // Find the original line item in the groups and replace it with the edited one
      this.groups.forEach(group => {
        group.lineItems.forEach((li, index) => {
          if (li.id === lineItem.id) {
            group.lineItems.splice(index, 1, deepCopyLineItem(lineItem));

            // Re-sort the line items if necessary
            if (lineItem.dateChanged) {
              invoiceEditorStore.sortGroup(group);
            }
          }
        });
      });

      // Recalculate totals after updating the line item
      this.updateLineItemsTotals(lineItem);
    }
  }
});